/**
 * aiJsonParser — shared utility for extracting JSON from AI responses.
 *
 * Different Claude models (Sonnet, Haiku, future models) return JSON
 * in different formats even when instructed to return only JSON.
 * This utility handles all known formats so model changes never break callers.
 *
 * Strategies tried in order:
 *   1. Direct JSON.parse (ideal — model followed instructions)
 *   2. Strip markdown code fences ```json ... ``` or ``` ... ```
 *   3. Find first { ... } block anywhere in the response
 *
 * Usage:
 *   import { extractAIJson } from '../../utils/aiJsonParser'
 *   const obj = extractAIJson(result.response)
 *   if (!obj) { // handle failure }
 */

/**
 * Extract a JSON object from an AI response string.
 * @param {string} raw - Raw text from AI (result.response or result.code)
 * @returns {object|null} Parsed object or null if all strategies fail
 */
export function extractAIJson(raw) {
  if (!raw || typeof raw !== 'string') return null
  const text = raw.trim()

  // Strategy 1: direct JSON parse
  try {
    const obj = JSON.parse(text)
    if (obj && typeof obj === 'object') return obj
  } catch (_) { /* try next */ }

  // Strategy 2: strip markdown code fences (```json...``` or ```...```)
  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/)
  if (fenceMatch) {
    try {
      const obj = JSON.parse(fenceMatch[1].trim())
      if (obj && typeof obj === 'object') return obj
    } catch (_) { /* try next */ }
  }

  // Strategy 3: find first { ... } JSON block anywhere in the response
  const braceStart = text.indexOf('{')
  const braceEnd   = text.lastIndexOf('}')
  if (braceStart !== -1 && braceEnd > braceStart) {
    try {
      const obj = JSON.parse(text.slice(braceStart, braceEnd + 1))
      if (obj && typeof obj === 'object') return obj
    } catch (_) { /* all strategies exhausted */ }
  }

  console.warn(' [aiJsonParser] all strategies failed. Raw (first 300 chars):', text.slice(0, 300))
  return null
}

/**
 * Extract a patch object from an AI result.
 * Tries result.response first, then result.code.
 * Validates that the object has start_line + replacement_text (patch shape).
 * @param {object} result - { response, code } from customPromptAI / callAI
 * @returns {object|null} Valid patch object or null
 */
export function extractAIPatch(result) {
  if (!result) return null
  for (const src of [result.response, result.code]) {
    if (!src) continue
    const obj = extractAIJson(src)
    if (obj && typeof obj.start_line === 'number' && obj.replacement_text !== undefined) {
      return obj
    }
  }
  return null
}
